import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Mail, MessageSquare, CheckCircle, Tag, MoreHorizontal, X, AlertTriangle, Trash2, Filter, Sparkles } from 'lucide-react';
import { buyerGroupsAPI } from '../services/api';
import './ProspectsActionToolbar.css';

const ProspectsActionToolbar = ({
  selectedCount,
  selectedBuyerIds = [],
  buyersData = [],
  onSendEmail,
  onSendSMS,
  onVerifyEmails,
  onResearchSelected,
  onMarkDNC,
  onRemoveDNC,
  onDeleteSelected,
  onClearSelection,
  onGroupActionSuccess,
  bulkVerifying = false,
  onOpenFilter,
  onManageGroups,
  filteredCount = 0,
  totalCount = 0,
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Group action states
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [removeGroupIds, setRemoveGroupIds] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [loading, setLoading] = useState(false);

  const dropdownRef = useRef(null);

  // Which groups the selected prospects are in (from the loaded rows' buyer.groups).
  // common = groups every selected prospect is in; options = groups any of them is in.
  // If some selected ids are not among the loaded rows we cannot know their groups,
  // so every group is offered and nothing is preselected.
  const removeInfo = useMemo(() => {
    const byId = new Map(buyersData.map((b) => [String(b.id), b]));
    const selected = selectedBuyerIds.map((id) => byId.get(String(id)));
    const known = selected.filter(Boolean);
    const allKnown = known.length === selectedBuyerIds.length && known.length > 0;
    const catalog = new Map(groups.map((g) => [String(g.id), g]));
    const counts = new Map();
    known.forEach((b) => {
      const seen = new Set();
      (Array.isArray(b.groups) ? b.groups : []).forEach((g) => {
        const key = String(g.id);
        if (seen.has(key)) return;
        seen.add(key);
        const entry = counts.get(key) || { id: key, name: g.name, color: g.color, count: 0 };
        entry.count += 1;
        counts.set(key, entry);
      });
    });
    if (!allKnown) {
      return {
        options: groups.map((g) => ({ id: String(g.id), name: g.name, color: g.color, count: null })),
        common: [],
        allKnown: false,
        total: selectedBuyerIds.length,
      };
    }
    const options = Array.from(counts.values()).map((entry) => {
      const fromCatalog = catalog.get(entry.id);
      return fromCatalog ? { ...entry, name: fromCatalog.name, color: fromCatalog.color || entry.color } : entry;
    });
    options.sort((a, b) => (b.count - a.count) || String(a.name).localeCompare(String(b.name)));
    const common = options.filter((o) => o.count === known.length).map((o) => o.id);
    return { options, common, allKnown: true, total: known.length };
  }, [buyersData, selectedBuyerIds, groups]);

  // Default the removal to the group(s) shared by all selected prospects whenever
  // the Groups menu opens or the selection changes while it is open.
  const commonKey = removeInfo.common.join(',');
  useEffect(() => {
    if (openDropdown === 'groups') {
      setRemoveGroupIds(commonKey ? commonKey.split(',') : []);
    }
  }, [openDropdown, commonKey]);

  const toggleRemoveGroup = (id) => {
    setRemoveGroupIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchGroups = async () => {
    try {
      setLoadingGroups(true);
      const response = await buyerGroupsAPI.getAll();
      if (response.data && response.data.success === false) {
        setGroups([]);
        return;
      }
      const groupData = response.data?.groups || response.data || [];
      setGroups(Array.isArray(groupData) ? groupData : []);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  const toggleDropdown = (dropdown) => {
    const newState = openDropdown === dropdown ? null : dropdown;
    setOpenDropdown(newState);

    // Refetch groups when opening the groups dropdown to ensure fresh data
    if (newState === 'groups') {
      fetchGroups();
    }
  };

  const handleAddToGroup = async () => {
    if (!selectedGroupId) return;
    if (!selectedBuyerIds.length) return;

    try {
      setLoading(true);
      await buyerGroupsAPI.addMembers(parseInt(selectedGroupId), selectedBuyerIds);
      await fetchGroups(); // Refresh groups list
      onGroupActionSuccess && onGroupActionSuccess(`Added ${selectedBuyerIds.length} prospect(s) to group`);
      resetGroupStates();
      setOpenDropdown(null);
    } catch (err) {
      console.error('Error adding to group:', err);
      onGroupActionSuccess && onGroupActionSuccess(err.response?.data?.error || 'Failed to add prospects to group');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromGroup = async () => {
    if (!removeGroupIds.length) return;
    if (!selectedBuyerIds.length) return;

    // Same call as before (one removeMembers per group), repeated for each ticked group.
    let done = 0;
    try {
      setLoading(true);
      for (const groupId of removeGroupIds) {
        await buyerGroupsAPI.removeMembers(parseInt(groupId), selectedBuyerIds);
        done += 1;
      }
      await fetchGroups(); // Refresh groups list
      onGroupActionSuccess && onGroupActionSuccess(
        removeGroupIds.length === 1
          ? `Removed ${selectedBuyerIds.length} prospect(s) from group`
          : `Removed ${selectedBuyerIds.length} prospect(s) from ${removeGroupIds.length} groups`
      );
      resetGroupStates();
      setOpenDropdown(null);
    } catch (err) {
      console.error('Error removing from group:', err);
      const base = err.response?.data?.error || 'Failed to remove prospects from group';
      onGroupActionSuccess && onGroupActionSuccess(
        done > 0 ? `${base} (removed from ${done} of ${removeGroupIds.length} groups)` : base
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      onGroupActionSuccess && onGroupActionSuccess('Please enter a group name');
      return;
    }
    if (!selectedBuyerIds.length) return;

    try {
      setLoading(true);
      await buyerGroupsAPI.bulkCreateAndAssign(
        newGroupName,
        selectedBuyerIds,
        '',
        '#3B82F6'
      );
      onGroupActionSuccess && onGroupActionSuccess(`Created group "${newGroupName}" with ${selectedBuyerIds.length} prospect(s)`);
      resetGroupStates();
      setOpenDropdown(null);
      await fetchGroups();
    } catch (err) {
      console.error('Error creating group:', err);
      onGroupActionSuccess && onGroupActionSuccess(err.response?.data?.error || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  const resetGroupStates = () => {
    setSelectedGroupId('');
    setRemoveGroupIds([]);
    setNewGroupName('');
  };

  return (
    <div className="prospects-action-toolbar sticky">
      <div className="toolbar-container">
        <div className="toolbar-left">
          <button
            className="toolbar-btn outline"
            onClick={onOpenFilter}
            title="Open filters"
          >
            <Filter size={16} />
            Filter
          </button>
          <button
            className="toolbar-btn outline"
            onClick={onManageGroups}
            title="Manage groups"
          >
            <Tag size={16} />
            Manage Groups
          </button>
          {selectedCount > 0 ? (
            <>
              <span className="toolbar-divider" />
              <span className="selection-badge">{selectedCount}</span>
              <span className="selection-text">selected</span>
            </>
          ) : (
            <>
              <span className="toolbar-divider" />
              <span className="selection-text prospect-count-text">
                {filteredCount} of {totalCount} prospects
              </span>
            </>
          )}
        </div>

        <div className="toolbar-actions" ref={dropdownRef}>
          {/* Outreach Dropdown */}
          <div className="action-dropdown">
            <button
              className="toolbar-btn primary"
              onClick={() => toggleDropdown('outreach')}
              disabled={selectedCount === 0}
            >
              <Mail size={16} />
              Outreach
              <span className="dropdown-arrow">▼</span>
            </button>
            {openDropdown === 'outreach' && (
              <div className="dropdown-menu">
                <button className="dropdown-item" onClick={() => { onSendEmail(); setOpenDropdown(null); }}>
                  <Mail size={14} />
                  Send Email
                </button>
                <button className="dropdown-item" onClick={() => { onSendSMS(); setOpenDropdown(null); }}>
                  <MessageSquare size={14} />
                  Send SMS
                </button>
              </div>
            )}
          </div>

          {/* Groups Dropdown */}
          <div className="action-dropdown">
            <button
              className="toolbar-btn success"
              onClick={() => toggleDropdown('groups')}
              disabled={selectedCount === 0}
            >
              <Tag size={16} />
              Groups
              <span className="dropdown-arrow">▼</span>
            </button>
            {openDropdown === 'groups' && (
              <div className="dropdown-menu groups-menu align-right">
                {/* Add to Group */}
                <div className="dropdown-section">
                  <div className="section-label">Add to Group</div>
                  <div className="inline-action">
                    <select
                      className="inline-select"
                      value={selectedGroupId}
                      onChange={(e) => setSelectedGroupId(e.target.value)}
                      disabled={loading || loadingGroups}
                    >
                      <option value="">
                        {loadingGroups ? 'Loading...' : 'Select group...'}
                      </option>
                      {groups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name} ({group.buyer_count || 0})
                        </option>
                      ))}
                    </select>
                    <button
                      className="inline-btn"
                      onClick={handleAddToGroup}
                      disabled={!selectedGroupId || loading}
                    >
                      {loading ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                </div>

                {/* Remove from Group */}
                <div className="dropdown-section">
                  <div className="section-label" id="remove-groups-label">Remove from Group</div>
                  {removeInfo.options.length === 0 ? (
                    <div className="inline-action">
                      <p className="group-remove-hint">
                        {loadingGroups ? 'Loading groups...' : 'None of the selected prospects are in a group.'}
                      </p>
                      <button className="inline-btn" type="button" disabled>
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="group-remove-list" role="group" aria-labelledby="remove-groups-label">
                        {removeInfo.options.map((g) => {
                          const checked = removeGroupIds.includes(g.id);
                          let countText = '';
                          if (g.count !== null) {
                            countText = g.count === removeInfo.total ? 'All selected' : `${g.count} of ${removeInfo.total}`;
                          }
                          return (
                            <label key={g.id} className={`group-remove-option${checked ? ' is-checked' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleRemoveGroup(g.id)}
                                disabled={loading}
                              />
                              <span className="group-remove-dot" style={{ '--group-color': g.color || undefined }} aria-hidden="true" />
                              <span className="group-remove-name">{g.name}</span>
                              {countText && <span className="group-remove-count">{countText}</span>}
                            </label>
                          );
                        })}
                      </div>
                      {removeInfo.common.length === 0 && (
                        <p className="group-remove-hint">
                          {removeInfo.allKnown
                            ? 'No group is shared by all selected prospects. Tick a group to remove the prospects that are in it.'
                            : 'Some selected prospects are not in the loaded list, so no group is preselected. Tick the group(s) to remove them from.'}
                        </p>
                      )}
                      <div className="group-remove-actions">
                        <button
                          className="inline-btn"
                          type="button"
                          onClick={handleRemoveFromGroup}
                          disabled={!removeGroupIds.length || loading}
                        >
                          {loading
                            ? 'Removing...'
                            : (removeGroupIds.length > 1 ? `Remove from ${removeGroupIds.length} groups` : 'Remove')}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Create New Group */}
                <div className="dropdown-section">
                  <div className="section-label">Create New Group</div>
                  <div className="inline-action">
                    <input
                      type="text"
                      className="inline-input"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      placeholder="Group name..."
                      maxLength={100}
                      disabled={loading}
                    />
                    <button
                      className="inline-btn"
                      onClick={handleCreateGroup}
                      disabled={!newGroupName.trim() || loading}
                    >
                      {loading ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* AI Research */}
          <button
            type="button"
            className="toolbar-btn secondary"
            onClick={onResearchSelected}
            disabled={selectedCount === 0}
            title="Score the selected prospects with AI research"
          >
            <Sparkles size={16} />
            AI Research
          </button>

          {/* More Actions Dropdown */}
          <div className="action-dropdown">
            <button
              className="toolbar-btn secondary"
              onClick={() => toggleDropdown('more')}
              disabled={selectedCount === 0}
            >
              <MoreHorizontal size={16} />
              More
              <span className="dropdown-arrow">▼</span>
            </button>
            {openDropdown === 'more' && (
              <div className="dropdown-menu">
                <button
                  className="dropdown-item"
                  onClick={() => { onVerifyEmails(); setOpenDropdown(null); }}
                  disabled={bulkVerifying}
                >
                  <CheckCircle size={14} />
                  {bulkVerifying ? 'Verifying...' : 'Verify Emails'}
                </button>
                <div className="dropdown-divider"></div>
                <button
                  className="dropdown-item warning"
                  onClick={() => { onMarkDNC(); setOpenDropdown(null); }}
                >
                  <AlertTriangle size={14} />
                  Mark as DNC
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => { onRemoveDNC(); setOpenDropdown(null); }}
                >
                  <CheckCircle size={14} />
                  Remove DNC
                </button>
                <div className="dropdown-divider"></div>
                <button
                  className="dropdown-item danger"
                  onClick={() => { onDeleteSelected(); setOpenDropdown(null); }}
                >
                  <Trash2 size={14} />
                  Delete Selected
                </button>
              </div>
            )}
          </div>

          {selectedCount > 0 && (
            <button
              className="toolbar-btn clear"
              onClick={onClearSelection}
              title="Clear selection"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProspectsActionToolbar;
